import { useId, useState } from 'react'
import { AlertCircle, CheckCircle2, HelpCircle, Loader2, UserPlus } from 'lucide-react'
import type { StaffConsultation, StaffConsultationAnswerRequest } from '@/services/conversationsApi'

const MAX_ANSWER_CHARS = 1000

const TOPIC_LABEL: Record<StaffConsultation['topic'], string> = {
  delivery_fee: 'Costo de delivery',
  general: 'Pregunta del cliente',
  catalog: 'Producto o disponibilidad',
}

export interface StaffConsultationCardProps {
  consultation: StaffConsultation
  isAnswering: boolean
  isTakingOver: boolean
  error: string | null
  onAnswer: (payload: StaffConsultationAnswerRequest) => void
  onTakeOver: () => void
}

function readAddress(consultation: StaffConsultation): string {
  const address = consultation.context?.['address']
  return typeof address === 'string' ? address : ''
}

/**
 * Duda que el asistente no pudo resolver. El vendedor responde al cliente y el asistente continúa con el
 * pedido, o toma el chat. Se evitan términos técnicos: el equipo no necesita saber cómo funciona el sistema.
 */
export function StaffConsultationCard({
  consultation,
  isAnswering,
  isTakingOver,
  error,
  onAnswer,
  onTakeOver,
}: StaffConsultationCardProps) {
  const fieldId = useId()
  const isDelivery = consultation.topic === 'delivery_fee'
  const address = readAddress(consultation)
  // La duda se muestra como una pregunta concreta para el equipo, no solo como el mensaje que escribió el cliente.
  const questionText = isDelivery
    ? `¿Cuánto cuesta el delivery a ${address ? `«${address}»` : 'esta dirección'}?`
    : consultation.question
  const customerMessage = consultation.customerMessage?.trim() ?? ''

  const [message, setMessage] = useState('')
  const [fee, setFee] = useState('')
  const [saveZone, setSaveZone] = useState(false)
  const [zoneName, setZoneName] = useState(address)
  const [confirmingTakeOver, setConfirmingTakeOver] = useState(false)

  const busy = isAnswering || isTakingOver
  const parsedFee = fee.trim() === '' ? null : Number(fee.replace(',', '.'))
  const feeIsValid = parsedFee === null || (Number.isFinite(parsedFee) && parsedFee >= 0)
  const zoneIsValid = !saveZone || (parsedFee !== null && feeIsValid && zoneName.trim().length > 0)
  const canSend = message.trim().length > 0 && feeIsValid && zoneIsValid && !busy

  const handleSend = () => {
    if (!canSend) return
    onAnswer({
      message: message.trim(),
      ...(isDelivery && parsedFee !== null ? { deliveryFeeUsd: parsedFee } : {}),
      ...(isDelivery && saveZone ? { saveZone: true, zoneName: zoneName.trim() } : {}),
      ...(isDelivery && !saveZone && address ? { zoneName: address } : {}),
    })
  }

  return (
    <section
      aria-label="Duda por resolver"
      className="mb-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 p-3.5 shadow-xs"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <HelpCircle className="h-4 w-4" aria-hidden="true" />
          </span>
          <h3 className="text-xs font-bold text-amber-950">Hay una duda por resolver</h3>
        </div>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
          {TOPIC_LABEL[consultation.topic]}
        </span>
      </div>

      <p className="mt-2 text-xs leading-relaxed text-amber-900">
        El asistente no tiene este dato y le avisó al cliente que lo está confirmando con el equipo.
      </p>
      <p className="mt-1.5 rounded-lg bg-white/80 px-2.5 py-2 text-sm font-semibold text-slate-900">{questionText}</p>
      {customerMessage && customerMessage !== questionText && (
        <p className="mt-1.5 text-[11px] text-amber-800">
          Lo que escribió el cliente: <span className="font-medium">«{customerMessage}»</span>
        </p>
      )}

      <div className="mt-3 space-y-2.5">
        <div>
          <label htmlFor={`${fieldId}-message`} className="block text-[11px] font-semibold text-slate-700">
            Tu respuesta para el cliente
          </label>
          <textarea
            id={`${fieldId}-message`}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            maxLength={MAX_ANSWER_CHARS}
            rows={3}
            disabled={busy}
            placeholder={isDelivery ? 'Ej.: Para esa zona el delivery cuesta $5.' : 'Escribe la respuesta que verá el cliente'}
            className="mt-1 w-full rounded-lg border border-amber-200 bg-white px-2.5 py-2 text-xs text-slate-900 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200 disabled:opacity-60"
          />
        </div>

        {isDelivery && (
          <div className="rounded-lg border border-amber-100 bg-white/60 p-2.5">
            <label htmlFor={`${fieldId}-fee`} className="block text-[11px] font-semibold text-slate-700">
              Costo del delivery en dólares (opcional)
            </label>
            <input
              id={`${fieldId}-fee`}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={fee}
              onChange={(event) => setFee(event.target.value)}
              disabled={busy}
              placeholder="5"
              className="mt-1 w-32 rounded-lg border border-amber-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200 disabled:opacity-60"
            />
            <p className="mt-1 text-[11px] text-slate-500">
              Si lo indicas, el asistente lo suma al total del pedido sin equivocarse.
            </p>
            {!feeIsValid && (
              <p role="alert" className="mt-1 text-[11px] font-medium text-rose-700">
                Escribe un monto válido, por ejemplo 5 o 5.50.
              </p>
            )}

            <label className="mt-2 flex items-start gap-2 text-[11px] text-slate-700">
              <input
                type="checkbox"
                checked={saveZone}
                onChange={(event) => setSaveZone(event.target.checked)}
                disabled={busy}
                className="mt-0.5 h-3.5 w-3.5 rounded border-amber-300"
              />
              <span>Guardar esta zona con su costo para que el asistente no vuelva a preguntar.</span>
            </label>
            {saveZone && (
              <div className="mt-1.5">
                <label htmlFor={`${fieldId}-zone`} className="block text-[11px] font-semibold text-slate-700">
                  Nombre de la zona
                </label>
                <input
                  id={`${fieldId}-zone`}
                  type="text"
                  value={zoneName}
                  onChange={(event) => setZoneName(event.target.value)}
                  maxLength={80}
                  disabled={busy}
                  className="mt-1 w-full rounded-lg border border-amber-200 bg-white px-2.5 py-1.5 text-xs text-slate-900 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200 disabled:opacity-60"
                />
                {!zoneIsValid && (
                  <p role="alert" className="mt-1 text-[11px] font-medium text-rose-700">
                    Para guardar la zona indica su nombre y el costo del delivery.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-2 flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs font-medium text-rose-800">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        {confirmingTakeOver ? (
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Confirmar que atenderás el chat">
            <span className="text-[11px] text-slate-700">El asistente dejará de responder en este chat.</span>
            <button
              type="button"
              onClick={onTakeOver}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {isTakingOver ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />}
              <span>{isTakingOver ? 'Un momento...' : 'Sí, atender yo'}</span>
            </button>
            <button
              type="button"
              onClick={() => setConfirmingTakeOver(false)}
              disabled={busy}
              className="rounded-lg px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-white/70"
            >
              Cancelar
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmingTakeOver(true)}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-50 disabled:opacity-50"
          >
            <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
            <span>Atender yo este chat</span>
          </button>
        )}

        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3.5 py-1.5 text-xs font-bold text-white shadow-xs hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isAnswering ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />}
          <span>{isAnswering ? 'Enviando...' : 'Responder y que el asistente continúe'}</span>
        </button>
      </div>
    </section>
  )
}
